import React, { useState, useEffect } from "react";
import {
  Container, Row, Col, Card, CardBody, CardTitle,
  Nav, NavItem, NavLink, TabContent, TabPane,
  Button, Input, Label, FormGroup, Spinner,
  Pagination, PaginationItem, PaginationLink
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { getCaseChunksAPI } from "../../../helpers/fakebackend_helper";

import { getAuthUser } from "../../../helpers/utils";


const hideFields = {};


/* ──────────────── Reusable Components ──────────────── */
function RecursiveEditor ({ value = '', path = [], onChange, name, keys, readOnly = true }) {
  const { t } = useTranslation();
  const isObject = value && typeof value === "object" && !Array.isArray(value);
  const isArray = Array.isArray(value);

  const handlePrimitiveChange = (newVal) => {
    onChange(path, newVal);
  };


  if (isObject) {
    const objectKeys = keys ? Object.keys(value).filter((k) => !keys.includes(k)) : Object.keys(value);
    return (
      <div style={{ borderLeft: "2px solid #eee", paddingLeft: 12, marginBottom: 8 }}>
        {name && <div style={{ fontWeight: 600, marginBottom: 6 }}>{t(name)}</div>}
        {objectKeys.map((key) => (
          <RecursiveEditor
            key={key}
            keys={keys}
            name={key}
            value={value[key]}
            path={[...path, key]}
            onChange={onChange}
            readOnly={readOnly}
          />
        ))}
      </div>
    );
  }

  else if (isArray) {
    // 🧱 Default array rendering (non-"articles")
    return (
      <div style={{ borderLeft: "2px dashed #eee", paddingLeft: 12, marginBottom: 8 }}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>
          {t(name)} ({t('length')}: {value.length})
        </div>
        {value.map((item, idx) => (
          <div key={idx} style={{ marginBottom: 8 }}>
            <RecursiveEditor
              name={`[${idx}]`}
              value={item}
              path={[...path, idx]}
              onChange={onChange}
              keys={keys}
              readOnly={readOnly}
            />
            {!readOnly && (
              <div>
                <Button
                  size="sm"
                  color="danger"
                  outline
                  onClick={() => {
                    const newArr = [...value];
                    newArr.splice(idx, 1);
                    onChange(path, newArr);
                  }}
                >
                  {t('Remove')}
                </Button>
              </div>
            )}
          </div>
        ))}
        {!readOnly && (
          <div style={{ marginTop: 6 }}>
            <Button
              size="sm"
              color="primary"
              outline
              onClick={() => {
                const newArr = [...value, typeof value[0] === "object" ? {} : ""];
                onChange(path, newArr);
              }}
            >
              {t('Add')}
            </Button>
          </div>
        )}
      </div>
    );
  }

  else {
    const asString = value === null ? "" : String(value);
    const useTextarea = asString.length > 60 || asString.includes("\n");

    return (
      <FormGroup style={{ marginBottom: 8 }}>
        <Label style={{ fontWeight: 600, fontSize: 14 }}>{t(name)}</Label>
        {useTextarea ? (
          <Input
            type="textarea"
            value={asString}
            rows={5}
            onChange={(e) => handlePrimitiveChange(e.target.value)}
            readOnly={readOnly}
          />
        ) : (
          <Input
            type="text"
            value={asString}
            onChange={(e) => handlePrimitiveChange(e.target.value)}
            readOnly={readOnly}
          />
        )}
      </FormGroup>
    );
  }
}

const PaginationControl = ({ total, current, onChange }) => {
  const pages = getPagination(total, current);

  return (
    pages.length > 1 &&
    <Pagination>
      <PaginationItem disabled={current === 0}>
        <PaginationLink previous onClick={() => onChange(current - 1)} />
      </PaginationItem>
      {pages.map((p, i) => (
        <PaginationItem key={i} active={p === current} disabled={p === "..."}>
          <PaginationLink onClick={() => typeof p === "number" && onChange(p)}>{p === "..." ? "..." : p + 1}</PaginationLink>
        </PaginationItem>
      ))}
      <PaginationItem disabled={current === total - 1}>
        <PaginationLink next onClick={() => onChange(current + 1)} />
      </PaginationItem>
    </Pagination>
  );
};


/* ──────────────── Helpers ──────────────── */
const getPagination = (total, current, maxVisible = 7) => {
  if (total <= 1) return [];
  const pages = [0];
  if (total <= maxVisible) return [...pages, ...Array.from({ length: total - 1 }, (_, i) => i + 1)];

  let start = Math.max(1, current - 2);
  let end = Math.min(total - 2, current + 2);
  if (end - start + 1 < maxVisible - 2) {
    current <= Math.floor(total / 2)
      ? (end = Math.min(total - 2, start + (maxVisible - 4)))
      : (start = Math.max(1, end - (maxVisible - 4)));
  }

  if (start > 1) pages.push("...");
  for (let i = start; i <= end; i++) pages.push(i);
  if (end < total - 2) pages.push("...");
  return [...pages, total - 1];
};

function updateAtPath(obj, path, newValue) {
  if (path.length === 0) {
    return newValue;
  }
  const [head, ...rest] = path;
  let clone;
  if (Array.isArray(obj)) clone = [...obj];
  else if (obj && typeof obj === "object") clone = { ...obj };
  else {
    clone = typeof head === "number" ? [] : {};
  }

  clone[head] = updateAtPath(clone[head], rest, newValue);
  return clone;
}

/* ──────────────── Main Component ──────────────── */
const CaseChunks = () => {
  const { t, i18n } = useTranslation();
  const { user } = getAuthUser();
  const navigate = useNavigate();
  const { state = {} } = useLocation();

  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [caseIndex, setCaseIndex] = useState(0);
  const [activeTab, setActiveTab] = useState("metadata");


  /* ─── Helpers ─── */
  const onChangeValue = (path, newVal) => {
    const updated = updateAtPath(data, path, newVal);
    setData(updated);
    if (user.RoleID === 118) debouncedSave(updated);
  };

  const exportJSON = () => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = state.FileName?.replace(".pdf", ".json") || "document.json";
    link.click();
  };

  /* ─── Fetch JSON & Users ─── */
  useEffect(() => {
    (async () => {
      if (!state.DocID) return;
      const response = await getCaseChunksAPI({ CaseID: state.DocID });
      setData(response.data || []);
      setIsLoading(false);
    })();
  }, [state.DocID]);

  const selectedCase = data[caseIndex] || {};

  const sectionTabs = [
    { id: "metadata", label: t("MetaData") },
  ];

  /* ──────────────── UI ──────────────── */
  return (
    <div className="page-content">
      <Container fluid>
        <Card>
          <CardBody>
            <CardTitle tag="h4">{state.FileName}</CardTitle>

            {/* Top Action Bar */}
            <Row className="align-items-center mb-3">
              <Col md="6">
                <Button color="secondary" onClick={() => navigate(-1)}>
                  <i className={`ri-arrow-${i18n.language === "en" ? "left" : "right"}-line`} />
                </Button>
              </Col>
              <Col md="6" className="text-end gap-2">
                {[1, 4, 98].includes(user.RoleID) && (
                  <div className="d-flex justify-content-end gap-2">
                    <Button onClick={exportJSON} disabled={isLoading}>{t("Export JSON")}</Button>
                  </div>
                )}
              </Col>
            </Row>

            { isLoading ? 
              <div className="d-flex justify-content-center align-items-center">
                <Spinner
                  style={{ height: "3rem", width: "3rem" }}
                  className="me-2"
                >
                  Loading...
                </Spinner>
              </div> : <>
                {/* Tabs */}
                <Nav tabs>
                  {sectionTabs.map(tab => (
                    <NavItem key={tab.id}>
                      <NavLink
                        className={activeTab === tab.id ? "active" : ""}
                        onClick={() => setActiveTab(tab.id)}
                        style={{ cursor: "pointer" }}
                      >
                        {tab.label}
                      </NavLink>
                    </NavItem>
                  ))}
                </Nav>

                <TabContent activeTab={activeTab} className="mt-3">

                  <TabPane tabId="metadata">
                    <Row className="justify-content-center mt-3" style={{ height: "80vh" }}>
                      <Col md="6"
                        style={{
                          height: "100%",
                          overflowY: "auto", // vertical scrollbar for PDF
                        }}>

                        <RecursiveEditor
                          name="metadata"
                          value={selectedCase}
                          path={[caseIndex]}
                          onChange={onChangeValue} 
                        />

                        { data.length ?
                          <PaginationControl
                            total={data.length}
                            current={caseIndex}
                            onChange={p => setCaseIndex(p)}
                          /> : null
                        }
                        
                      </Col>
                      <Col md="6"
                        style={{
                          height: "100%",
                          overflowY: "auto",
                          overflowX: "hidden",
                        }}>
                        <iframe
                          src={state.FileURL || ''}
                          width="100%"
                          height="95%"
                          title="PDF Viewer"
                          style={{ minHeight: "570px" }}
                          ></iframe>
                      </Col>
                    </Row>
                  </TabPane>
                </TabContent>
              </>
            }
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default CaseChunks;
